import { Checkbox as AntCheckbox, CheckboxProps as AntCheckboxProps } from 'antd'

export interface CheckboxProps extends AntCheckboxProps {
  /**
   * Checkbox label
   */
  label?: string
  /**
   * Size of the checkbox text
   * @default 'small'
   */
  size?: 'small' | 'default'
}

/**
 * Checkbox Component
 *
 * A checkbox input with optional label.
 *
 * @example
 * <Checkbox label="I agree to the terms and conditions" />
 *
 * @example
 * // Controlled checkbox
 * <Checkbox
 *   checked={isChecked}
 *   onChange={(e) => setIsChecked(e.target.checked)}
 * >
 *   Subscribe to newsletter
 * </Checkbox>
 */
export function Checkbox({ label, children, size = 'small', className, ...props }: CheckboxProps) {
  const sizeClass = size === 'small' ? 'text-sm' : ''
  const combinedClass = [sizeClass, className].filter(Boolean).join(' ')

  return <AntCheckbox className={combinedClass || undefined} {...props}>{label || children}</AntCheckbox>
}

export interface CheckboxGroupProps extends React.ComponentProps<typeof AntCheckbox.Group> {
  /**
   * Size of the checkbox text
   * @default 'small'
   */
  size?: 'small' | 'default'
}

/**
 * CheckboxGroup Component
 *
 * A group of checkboxes with optional size control.
 *
 * @example
 * <CheckboxGroup
 *   options={[{ label: 'Option 1', value: '1' }, { label: 'Option 2', value: '2' }]}
 *   value={selectedValues}
 *   onChange={setSelectedValues}
 *   size="small"
 * />
 */
export function CheckboxGroup({ size = 'small', className, ...props }: CheckboxGroupProps) {
  const sizeClass = size === 'small' ? '[&_.ant-checkbox-wrapper]:text-sm' : ''
  const combinedClass = [sizeClass, className].filter(Boolean).join(' ')

  return <AntCheckbox.Group className={combinedClass || undefined} {...props} />
}
